/**
 * Import definitions (TO-414, Epic 25 F5): versioned, declarative contracts
 * that say how one logical file is parsed, mapped onto a canonical schema,
 * validated and processed. F5 stores and structurally checks them; the
 * loaders, transforms, validators and processors that execute them are F6–F9.
 */

/** A version's lifecycle: `DRAFT → ACTIVE → RETIRED`, nothing else. */
export enum ImportDefinitionStatus {
	DRAFT = 'DRAFT',
	ACTIVE = 'ACTIVE',
	RETIRED = 'RETIRED',
}

export enum ImportFormatType {
	FIXED_WIDTH = 'fixed-width',
	CSV = 'csv',
	JSON = 'json',
}

export enum ImportSourceEncoding {
	UTF_8 = 'utf-8',
	ISO_8859_1 = 'iso-8859-1',
	WINDOWS_1252 = 'windows-1252',
}

export enum ImportLineEnding {
	AUTO = 'auto',
	LF = 'lf',
	CRLF = 'crlf',
}

/** The primitive type of a parsed source field. */
export enum ImportSourceFieldType {
	STRING = 'string',
	INTEGER = 'integer',
	DECIMAL = 'decimal',
	BOOLEAN = 'boolean',
	DATE = 'date',
}

/** The declarative transforms of epic spec §12. No executable code exists. */
export enum ImportTransformType {
	TRIM = 'trim',
	UPPERCASE = 'uppercase',
	LOWERCASE = 'lowercase',
	SUBSTRING = 'substring',
	REPLACE = 'replace',
	REGEX_REPLACE = 'regexReplace',
	PAD_START = 'padStart',
	PAD_END = 'padEnd',
	EMPTY_TO_NULL = 'emptyToNull',
	TO_STRING = 'toString',
	TO_INTEGER = 'toInteger',
	TO_DECIMAL = 'toDecimal',
	TO_BOOLEAN = 'toBoolean',
	PARSE_DATE = 'parseDate',
	PARSE_DATE_TIME = 'parseDateTime',
	FORMAT_DATE = 'formatDate',
	LOOKUP = 'lookup',
	DEFAULT = 'default',
	COALESCE = 'coalesce',
	MULTIPLY = 'multiply',
	DIVIDE = 'divide',
	ROUND = 'round',
	CONCAT = 'concat',
	TEMPLATE = 'template',
}

export enum ImportRecordRuleType {
	REQUIRED = 'required',
	PATTERN = 'pattern',
	MIN_LENGTH = 'minLength',
	MAX_LENGTH = 'maxLength',
	MIN = 'min',
	MAX = 'max',
	ONE_OF = 'oneOf',
}

export enum ImportDatasetRuleType {
	UNIQUE = 'unique',
	REFERENCE = 'reference',
	NO_CYCLES = 'noCycles',
	ROW_COUNT = 'rowCount',
}

/** The processing strategies of epic spec §16 (`full_load` was `snapshot` before TO-518). */
export enum ImportProcessingStrategy {
	REPLACE = 'replace',
	UPSERT = 'upsert',
	APPEND = 'append',
	FULL_LOAD = 'full_load',
}

/**
 * A source-system slug (TO-518 BR-B): the upstream system an import's records
 * come from. Receivers key their records on (tenant, external key, source system).
 */
export const SOURCE_SYSTEM_PATTERN = /^[a-z0-9][a-z0-9_-]{0,62}$/

/**
 * The keyed strategies (TO-522 BR-3): they match records by `processing.key`
 * within one source system, so a definition must name both, and an execution
 * without a source fails closed. The definition validator and the processor both
 * read this set — there is no second list (ADR-0053).
 */
export const KEYED_STRATEGIES: ReadonlySet<ImportProcessingStrategy> = new Set([
	ImportProcessingStrategy.UPSERT,
	ImportProcessingStrategy.FULL_LOAD,
])

declare const sourceSystemBrand: unique symbol

/**
 * A string proven to match {@link SOURCE_SYSTEM_PATTERN} (TO-522 BR-4). The
 * domain-importer port carries only this type, so a malformed slug cannot reach
 * a receiver. Build one with {@link toSourceSystem} or narrow with {@link isSourceSystem}.
 */
export type SourceSystem = string & { readonly [sourceSystemBrand]: 'SourceSystem' }

/** A value that is not a source-system slug was given to {@link toSourceSystem}. */
export class InvalidSourceSystemError extends Error {
	constructor(value: string) {
		super(`not a source-system slug: ${JSON.stringify(value)}`)
		this.name = 'InvalidSourceSystemError'
	}
}

export const isSourceSystem = (value: unknown): value is SourceSystem =>
	typeof value === 'string' && SOURCE_SYSTEM_PATTERN.test(value)

/** The slug as a {@link SourceSystem}; throws {@link InvalidSourceSystemError} when it does not match. */
export const toSourceSystem = (value: string): SourceSystem => {
	if (!isSourceSystem(value)) throw new InvalidSourceSystemError(value)
	return value
}

export enum ImportOnErrorStrategy {
	REJECT_FILE = 'reject-file',
	SKIP_INVALID_RECORDS = 'skip-invalid-records',
}

export enum ImportNotificationAction {
	NONE = 'none',
	ALERT = 'alert',
}

/** Why a referenced definition version cannot be used (spec REQ-010/REQ-011), in check order. */
export enum ImportDefinitionUnusableReason {
	NOT_FOUND = 'NOT_FOUND',
	NOT_ACTIVE = 'NOT_ACTIVE',
}

export interface CanonicalSchemaRef {
	name: string
	version: number
}

export interface FixedWidthField {
	name: string
	start: number
	length: number
	type: ImportSourceFieldType
	required: boolean
}

export interface CsvField {
	name: string
	/** The header name; set exactly when the format has `header: true`. */
	column: string | null
	/** The 1-based column; set exactly when the format has `header: false`. */
	position: number | null
	type: ImportSourceFieldType
	required: boolean
}

export interface JsonField {
	name: string
	path: string
	type: ImportSourceFieldType
	required: boolean
}

export interface FixedWidthFormat {
	type: ImportFormatType.FIXED_WIDTH
	encoding: ImportSourceEncoding
	lineEnding: ImportLineEnding
	skipLines: number
	fields: FixedWidthField[]
}

export interface CsvFormat {
	type: ImportFormatType.CSV
	encoding: ImportSourceEncoding
	lineEnding: ImportLineEnding
	skipLines: number
	delimiter: string
	quote: string
	header: boolean
	fields: CsvField[]
}

export interface JsonFormat {
	type: ImportFormatType.JSON
	encoding: ImportSourceEncoding.UTF_8
	recordsPath: string
	fields: JsonField[]
}

export type ImportFormat = FixedWidthFormat | CsvFormat | JsonFormat

/** A transform: its `type` plus exactly the params the spec lists for it. */
export interface ImportTransform {
	type: ImportTransformType
	[param: string]: unknown
}

export interface MappingField {
	sources: string[]
	target: string
	transforms: ImportTransform[]
}

export interface ImportRecordRule {
	type: ImportRecordRuleType
	value?: string | number
	values?: Array<string | number | boolean>
}

export interface RecordValidation {
	field: string
	rules: ImportRecordRule[]
}

export type ImportDatasetRule =
	| { type: ImportDatasetRuleType.UNIQUE; fields: string[] }
	| { type: ImportDatasetRuleType.REFERENCE; field: string; references: string; allowNull: boolean }
	| { type: ImportDatasetRuleType.NO_CYCLES; child: string; parent: string }
	| { type: ImportDatasetRuleType.ROW_COUNT; min: number | null; max: number | null }

/** The whole declarative contract of one definition version (spec Contract section). */
export interface ImportDefinitionContract {
	canonicalSchema: CanonicalSchemaRef
	file: { naming: { pattern: string }; format: ImportFormat }
	mapping: { fields: MappingField[] }
	validation: { record: RecordValidation[]; dataset: ImportDatasetRule[] }
	processing: {
		strategy: ImportProcessingStrategy
		key: string[]
		atomic: boolean
		onError: { strategy: ImportOnErrorStrategy }
		/** Required for `upsert`/`full_load`; null or absent otherwise means unsourced (TO-518 BR-C). */
		sourceSystem?: string | null
	}
	notification: { missingFile: ImportNotificationAction; invalidFile: ImportNotificationAction }
	retention: { originalFileDays: number; reportsDays: number }
}

export interface ImportDefinitionVersionSummaryDTO {
	version: number
	status: ImportDefinitionStatus
	canonicalSchema: CanonicalSchemaRef
	revision: number
	createdAt: string
	createdBy: string
	updatedAt: string
	updatedBy: string
	activatedAt: string | null
	activatedBy: string | null
	retiredAt: string | null
	retiredBy: string | null
}

export interface ImportDefinitionVersionDTO extends ImportDefinitionVersionSummaryDTO {
	key: string
	contract: ImportDefinitionContract
}

export interface ImportDefinitionDTO {
	key: string
	createdAt: string
	createdBy: string
	/** Newest first. */
	versions: ImportDefinitionVersionSummaryDTO[]
}

export interface ImportDefinitionListDTO {
	items: ImportDefinitionDTO[]
}

/** `POST /import-definitions` body. */
export interface ImportDefinitionCreateRequest {
	key: string
	contract: ImportDefinitionContract
}

/** `PUT …/versions/{version}` body: a full replacement guarded by the revision last read. */
export interface ImportDefinitionDraftUpdateRequest {
	expectedRevision: number
	contract: ImportDefinitionContract
}
